import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useLocation} from 'react-router-dom';

import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_continentsLow from "@amcharts/amcharts4-geodata/continentsLow";
import * as am4plugins_bullets from "@amcharts/amcharts4/plugins/bullets";

import { fetchThreatActors as fetchThreatActorsService } from 'services/modules/vapt/data';
import { setThreatActors as setThreatActorsSlice} from 'stores/modules/vapt/datareducer';

am4core.useTheme(am4themes_animated);

const ThreatActorGeography = (props) => {
  // useLocation Hook
  const location = useLocation();

  // Query params
  const searchParams = new URLSearchParams(location.search);
  const categorySlug = searchParams.get("category");
  const dimensionSlug = searchParams.get("dimension");

  // Redux dispatch
  const dispatch = useDispatch();

  // useRef hook
  const chartDivRef = useRef(null);

  // Redux selectors
  const reportDateContainer = useSelector((state) => state.vaptReport.latestReportDate);
  const threatActorsContainer = useSelector(state => state.vaptData.threatActors);
  const vaptDimensionsContainer = useSelector(state => state.vaptData.vaptDimensions);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  
  // useEffect hook initialises the chart data with the threatactors data
  useEffect(() =>{
    const mergedThreatActors = Object.values(
      threatActorsContainer.reduce((acc, actor) => {
        // If the country already exists in the accumulator, merge the name
        if (acc[actor.country]) {
          acc[actor.country].name += `\n ${actor.name}`;
        } else {
          // If the country doesn't exist, add it with current actor details
          acc[actor.country] = { ...actor };
        }
        return acc;
      }, {})
    );

    const chart = am4core.create(chartDivRef.current, am4maps.MapChart);
    chart.data = mergedThreatActors;
    chart.geodata = am4geodata_continentsLow;
    chart.projection = new am4maps.projections.Miller();
    chart.homeGeoPoint = {
      latitude: 25,
      longitude: 0
    }
    chart.maxZoomLevel = 1;
    chart.seriesContainer.draggable = false;
    chart.seriesContainer.resizable = false;

    // Create map polygon series
    let polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.exclude = ["antarctica"];
    polygonSeries.useGeodata = true;

    // Configure series
    let polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.fill = am4core.color("#454a58");

    // Add shadow
    let shadow = polygonSeries.filters.push(new am4core.DropShadowFilter());
    shadow.color = am4core.color("#60666b");
    shadow.blur = 0;

    // Pins
    let imageSeries = chart.series.push(new am4maps.MapImageSeries());
    let imageTemplate = imageSeries.mapImages.template;
    imageTemplate.propertyFields.longitude = "longitude";
    imageTemplate.propertyFields.latitude = "latitude";
    imageTemplate.nonScaling = true;

    // Creating a pin bullet
    let pin = imageTemplate.createChild(am4plugins_bullets.PinBullet);

    // Configuring pin appearance
    pin.background.fill = am4core.color("#FF5C5C");
    pin.background.pointerBaseWidth = 1;
    pin.background.pointerLength = 20;
    pin.background.radius = 5;
    pin.circle.fill = pin.background.fill;
    pin.tooltipText  = '{name}';
    imageSeries.data = chart.data;

    // Cleanup function
    return () => {
      chart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threatActorsContainer]);
  
  // useEffect hook fetches the threat actors data based on domain and date
  useEffect(() => {
    const fetchThreatActorsData = async() => {
      let params = {
        "domain":selectedCompanyContainer.domain,
        "date":reportDateContainer,
        "category":props?.category,
        "subcategory":props?.subcategory
      }
      const vaptCategories = vaptDimensionsContainer?.map((cat)=>cat.slug);
      if (vaptCategories.includes(categorySlug) && dimensionSlug !== null ){
        const categories = vaptDimensionsContainer?.find(cat => cat.slug === categorySlug);
        const dimensions = categories?.Dimensions?.filter((dim)=>dim.slug === dimensionSlug).map((dim)=> dim.id)
        params.dimension = dimensions[0];
      }
      dispatch(fetchThreatActorsService({ params }));
    };
    if (reportDateContainer && (vaptDimensionsContainer.length>0) && props?.category!=="" && props?.subcategory!==null ){
      fetchThreatActorsData();
    }
    else{
      dispatch(setThreatActorsSlice([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDateContainer, vaptDimensionsContainer]);

  return (
    <div>
      <p className="carousel-title">Threat Actor Location Overview</p>
      {threatActorsContainer?.length >0 ? (
        <div>
          <div ref={chartDivRef} id="chartdiv" className='threat-actor-data'></div>
        </div>
      ):(
        <div className="no-threatactors-data">
          <p className="carousel-no-data">No relevant threat actors found</p>
          <div ref={chartDivRef} id="chartdiv" className='no-threat-data'></div>
        </div>
      )}
    </div>
  )
}

export default ThreatActorGeography;